import { useCallback, useEffect, useRef } from "react";
import { useMissionControlSubscription } from "@/context/MissionControlWebSocketContext";

const RECONCILE_DEBOUNCE_MS = 300;

/** Edge-triggered refetch when the WS reconnects or the tab regains focus (no polling). */
export function useGuardiansReconcile(
  refresh: () => Promise<void>,
  enabled = true
): void {
  const { isConnected } = useMissionControlSubscription({});
  const wasConnectedRef = useRef(isConnected);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const scheduleRefresh = useCallback(() => {
    if (!enabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void refreshRef.current();
    }, RECONCILE_DEBOUNCE_MS);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const wasConnected = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (!wasConnected && isConnected) {
      scheduleRefresh();
    }
  }, [enabled, isConnected, scheduleRefresh]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    const onFocus = () => scheduleRefresh();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, scheduleRefresh]);
}
